"use client";

import { Printer, RotateCcw, Share2, FileJson } from "lucide-react";
import { toast } from "sonner";
import type { ReportData } from "./reportData";

interface ReportActionsProps {
  onReset: () => void;
  name: string;
  report: ReportData;
}

export default function ReportActions({ onReset, name, report }: ReportActionsProps) {
  const handlePrintToPDF = () => {
    window.print();
  };

  const handleExportJSON = () => {
    try {
      const exportData = {
        generatedAt: new Date().toISOString(),
        subject: name,
        report,
      };
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${name.replace(/\s+/g, "_")}_Avatar_Blueprint.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("JSON exported successfully!", {
        description: `${name}_Avatar_Blueprint.json downloaded.`,
        duration: 3000,
      });
    } catch {
      toast.error("Failed to export JSON. Please try again.", { duration: 4000 });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${name}'s QuantuMe Nautilus Avatar Blueprint`,
          text: "Discover your archetypal identity with QuantuMe Nautilus.",
          url: window.location.href,
        });
      } catch {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success("Link copied to clipboard!", { duration: 3000 });
    }).catch(() => {
      toast.error("Could not copy link. Please copy the URL manually.", { duration: 4000 });
    });
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 p-5 rounded-xl no-print"
      style={{
        background: "rgba(54,23,64,0.5)",
        border: "1px solid rgba(219,184,77,0.15)",
      }}
    >
      {/* Left: Reset */}
      <button
        onClick={onReset}
        className="btn-secondary flex items-center gap-2 text-sm"
      >
        <RotateCcw className="w-4 h-4" />
        <span>New Report</span>
      </button>

      {/* Right: Share / Export JSON / Print to PDF */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-all duration-150 hover:-translate-y-0.5"
          style={{
            background: "rgba(219,184,77,0.08)",
            border: "1px solid rgba(219,184,77,0.25)",
            color: "rgba(219,184,77,0.8)",
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(219,184,77,0.15)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(219,184,77,0.08)";
          }}
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>

        <button
          onClick={handleExportJSON}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-all duration-150 hover:-translate-y-0.5"
          style={{
            background: "rgba(219,184,77,0.08)",
            border: "1px solid rgba(219,184,77,0.25)",
            color: "rgba(219,184,77,0.8)",
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(219,184,77,0.15)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(219,184,77,0.08)";
          }}
        >
          <FileJson className="w-4 h-4" />
          <span className="hidden sm:inline">Export JSON</span>
        </button>

        <button
          onClick={handlePrintToPDF}
          className="btn-primary flex items-center gap-2 text-sm"
          style={{ padding: "0.625rem 1.25rem" }}
        >
          <Printer className="w-4 h-4" />
          <span>Print / PDF</span>
        </button>
      </div>
    </div>
  );
}